import { useTranslation } from 'next-i18next';
import cn from 'classnames';

import { titleCase } from '@utils/plp/utils/transforms';
import type { ColourOption } from '@adapters/bigcommerce/hooks/product/variantAvailability';
import ColourSwatchView from '_components/ColourPicker/ColourSwatch/ColourSwatchView';
import { GREIGE_HEX } from '_components/ColourPicker/ColourSwatch/useColourSwatchModel';
import type { ColourPickerColour } from '_components/ColourPicker/ColourPickerContext';

// Relative, not the `_components/` alias: that alias's moduleNameMapper entry
// is matched before the generic `.scss` → identity-obj-proxy one in jest's
// config, so an aliased .scss import bypasses the proxy and jest tries to
// parse raw Sass as JS. Every other `.module.scss` import in this codebase is
// relative for the same reason.
import styles from '../../ColourPicker/ColourSwatch/ColourSwatch.module.scss';

export interface QuickAddColourSwatchProps {
  colour: ColourOption;
  isSelected: boolean;
  onSelect: () => void;
  imageWidth?: number;
  imageHeight?: number;
}

// Matches `QuickAddPopover.module.scss`'s `.colourRow` --colour-swatch-size
// (the PDP's plain, non-card sizing — see that file's comment for why).
const DEFAULT_IMAGE_SIZE = 32;

/**
 * Quick Add's colour swatch cell (ED-355). Reuses the PDP picker's
 * presentational `ColourSwatchView` (image → hex-dot → no-data fill,
 * disabled/struck-through state) rather than reinventing that visual
 * language, but computes its own model directly from `useVariantSelection`'s
 * `ColourOption` — no `ColourPickerContext`, no `useColourSwatchModel`. Quick
 * Add has its own selection/stock source of truth (the popover's own hook);
 * pulling in the PDP's context here would tie two independent pickers
 * together for no benefit.
 *
 * Composes `ColourSwatch.module.scss`'s own classes (`.button`,
 * `.buttonSelected`, …) — never `QuickAddPopover.module.scss`'s classes on
 * the same element, so this isn't the same-element cross-module className
 * tie the CSS-consistency harness exists to catch. `QuickAddPopoverColourRow`
 * (the row wrapper, in `./index.tsx`) supplies this cell's `--colour-swatch-*`
 * sizing via inherited CSS custom properties rather than composed classes,
 * since those tokens live scoped to the PDP picker's own `.root`/`.rootCard`
 * wrapper, which this cell never renders inside.
 */
const QuickAddColourSwatch = ({
  colour,
  isSelected,
  onSelect,
  imageWidth = DEFAULT_IMAGE_SIZE,
  imageHeight = DEFAULT_IMAGE_SIZE,
}: QuickAddColourSwatchProps) => {
  const { t } = useTranslation('quickAdd');

  const hasImage = Boolean(colour.swatchImage);
  const firstHex = colour.hexColours?.[0];
  const hasHex = Boolean(firstHex);
  // Mirrors `useColourSwatchModel`'s own check (the PDP/PLP pickers): a
  // handful of colours carry this placeholder "greige" hex instead of no
  // hex at all, and PDP renders those as the dashed/no-data swatch rather
  // than literally filling them in — Quick Add must match that, not paint
  // a real grey-beige swatch for what's actually a "no data" colour.
  const isGreigeHex =
    firstHex?.toUpperCase().trim() === GREIGE_HEX.toUpperCase();
  const isNoData = !hasImage && (!hasHex || isGreigeHex);
  // Stays clickable when disabled (no native `disabled` attribute) so picking
  // it can still resolve a variant / reset the size — mirrors the PDP
  // picker's own out-of-stock swatch behaviour.
  const isDisabled = !colour.hasAnyStock;
  const baseName = titleCase(colour.name);
  // `ColourSwatchView` sets the button's `aria-label` to `displayColourName`
  // alone — per the accessible-name computation, an explicit `aria-label`
  // wins over any child content, so the "not available" text in its
  // screen-reader-only span (built from `disabledLabel`) is never actually
  // exposed to assistive tech. The suffix has to be folded in here instead;
  // `disabledLabel` is passed empty below so that span doesn't repeat it.
  const displayColourName = isDisabled
    ? `${baseName} (${t('quickAdd.colourNotAvailable')})`
    : baseName;

  const viewColour: ColourPickerColour = {
    id: colour.id,
    name: colour.name,
    swatchImage: colour.swatchImage,
    hexColours: colour.hexColours,
    isSelected,
    isDisabled,
    // Quick Add's popover has no promo/deal-badge concept surfaced by
    // useVariantSelection today (unlike the PDP picker, which reads
    // `colour.isDeal` off its own richer context) — always false, not a gap.
    isDeal: false,
  };

  return (
    <ColourSwatchView
      colour={viewColour}
      displayColourName={displayColourName}
      hasImage={hasImage}
      hasHex={hasHex}
      isNoData={isNoData}
      disabledLabel=""
      onSelect={onSelect}
      onPreload={() => {}}
      wrapperClassName={styles.wrapper}
      buttonClassName={cn(styles.button, {
        [styles.buttonSelected]: isSelected,
        [styles.buttonUnavailableForSize]: isDisabled,
        [styles.buttonNoData]: isNoData,
      })}
      imageWidth={imageWidth}
      imageHeight={imageHeight}
    />
  );
};

export default QuickAddColourSwatch;
